import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Toaster, toast } from 'sonner'
// Geist is the house typeface and every tracking, line-height and column width in
// the app is tuned to its metrics. The variable build ships one file per subset and
// covers weights 100-900, so no per-weight imports are needed.
import '@fontsource-variable/geist'
import '@/i18n/init'
import ApiClient from '@/domains/shared/hooks/ApiClient'
import { onSignOutElsewhere } from '@/domains/shared/hooks/authBroadcast'
import App from '@/domains/shared/routes/App'
import { hasReloadedForChunkRecently, stampChunkReload } from '@/domains/shared/utils/staleChunk'
import '@/styles/index.css'

// After a deploy the old index.html still references the previous build's
// hashed chunks, so importing one 404s and the tab is stuck on "Something went
// wrong" (retry re-imports the same missing file). Vite wraps every dynamic
// import in the build, so this fires for a lazy route and for a lazy component
// inside a page alike (an edit form's editor, a tab panel) - reload to pick up
// the new index.html. The sessionStorage timestamp stops a reload loop when
// the import keeps failing for another reason (the error then surfaces to the
// ErrorBoundary, which offers a manual reload).
window.addEventListener('vite:preloadError', (event) => {
  if (hasReloadedForChunkRecently()) return
  stampChunkReload()
  event.preventDefault()
  window.location.reload()
})

const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    if (mutation.meta?.skipGlobalErrorToast) return
    toast.error(error instanceof Error ? error.message : String(error))
  },
})

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
})

// Restore the session from the httpOnly session cookie in the background - first
// paint must not wait on a network round-trip. While `hydrating` is true,
// ProtectedRoute renders nothing and UserSignIn renders a splash; both settle
// once hydration flips the flag.
// hydrate() handles network failures internally; the catch only guards its
// dynamic store import so a failure can never surface as an unhandled rejection.
ApiClient.hydrate().catch(() => undefined)

// A sign-out in another tab ended the shared session; reloading drops this tab's
// access token and cached data and lands on the sign-in page.
onSignOutElsewhere(() => globalThis.location.reload())

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element with id "root" not found')
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
